import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Crown, AlertCircle, ArrowLeft, ShoppingBag, CalendarCheck, Sparkles, ShieldCheck } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { ScoreRing } from "../components/ScoreRing";
import { CrownCertificate } from "../components/CrownCertificate";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { FRESHA_URL, STORE_URL } from "../lib/constants";

const barColor = (s) => (s >= 7 ? "bg-gold" : s >= 4 ? "bg-cream" : "bg-destructive");

const SectionTitle = ({ icon: Icon = Crown, children }) => (
  <div className="mb-6 flex items-center gap-4">
    <span className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/40 text-gold">
      <Icon className="h-4 w-4" strokeWidth={1.5} />
    </span>
    <h2 className="font-heading text-3xl text-cream">{children}</h2>
    <span className="h-px flex-1 bg-gradient-to-r from-gold/40 to-transparent" />
  </div>
);

export default function Report() {
  const { scanId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scan, setScan] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/scans/${scanId}`).then((r) => setScan(r.data)).catch(() => setErr(true));
  }, [scanId]);

  if (err)
    return (
      <div className="min-h-screen bg-charcoal text-cream">
        <Navbar />
        <div className="mx-auto max-w-md px-6 py-32 text-center">
          <p className="text-muted-foreground">Report not found.</p>
          <Button className="mt-6 rounded-full bg-gold text-charcoal" onClick={() => navigate("/dashboard")}>Back to dashboard</Button>
        </div>
      </div>
    );

  if (!scan)
    return (
      <div className="flex min-h-screen items-center justify-center bg-charcoal">
        <Loader2 className="h-6 w-6 animate-spin text-gold" />
      </div>
    );

  const r = scan.result;
  const densityFlagged = r.categories?.some((c) => c.key === "density" && c.score <= 6);
  const anyFlagged = r.categories?.some((c) => c.score <= 6);

  return (
    <div className="min-h-screen bg-charcoal text-cream">
      <Navbar />
      <div className="mx-auto max-w-5xl px-6 py-12">
        <button onClick={() => navigate("/dashboard")} className="mb-8 flex items-center gap-2 text-sm text-muted-foreground hover:text-gold" data-testid="report-back-btn">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </button>

        {/* Certificate-style hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="relative overflow-hidden rounded-3xl border-2 border-gold/30 bg-charcoal-surface p-1"
        >
          <div className="relative rounded-[20px] border border-gold/15 p-8 md:p-12">
            <div className="absolute inset-0 opacity-[0.06]" style={{ background: "radial-gradient(circle at 50% -10%, #D4AF37, transparent 55%)" }} />
            <div className="relative flex flex-col items-center gap-8 text-center md:flex-row md:justify-between md:text-left">
              <div className="max-w-md">
                <div className="mb-4 flex items-center justify-center gap-3 md:justify-start">
                  <img src="/carlocs-logo-192.png" alt="Carlocs" className="h-12 w-12 object-contain" />
                  <span className="text-xs uppercase tracking-[0.35em] text-gold">Carlocs Scalp Report</span>
                </div>
                <h1 className="font-heading text-5xl leading-none text-cream md:text-6xl">Overall {r.overall_label}</h1>
                <div className="mx-auto mt-5 h-px w-40 bg-gradient-to-r from-transparent via-gold/60 to-transparent md:mx-0" />
                <p className="mt-5 text-sm text-muted-foreground">
                  Prepared {new Date(scan.created_at).toLocaleString(undefined, { dateStyle: "long", timeStyle: "short" })}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {scan.questionnaire?.wash_frequency ? `Washes ${scan.questionnaire.wash_frequency}` : "Scalp health report"}
                  {scan.questionnaire?.time_since_service ? ` · Last service ${scan.questionnaire.time_since_service}` : ""}
                </p>
                {scan.source === "in_salon" && (
                  <div className="mt-4 rounded-lg border border-gold/40 bg-charcoal/60 p-3 text-xs text-cream/90" data-testid="report-in-salon-info">
                    <span className="inline-flex items-center gap-1 font-500 text-gold"><ShieldCheck className="h-3.5 w-3.5" /> In-Salon Scan</span>
                    <p className="mt-1 text-muted-foreground">{scan.location} · performed by {scan.performed_by}</p>
                    <p className="text-muted-foreground">Payment: {scan.payment_status}</p>
                  </div>
                )}
                <div className="mt-6 flex items-start gap-2 rounded-lg border border-charcoal-border bg-charcoal/60 p-3 text-xs text-muted-foreground">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" />
                  These are expert observations to guide your loc care — not a medical diagnosis.
                </div>
              </div>
              <ScoreRing score={r.overall_score} label={r.overall_label} />
            </div>
          </div>
        </motion.div>

        {/* Photo + categories */}
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="overflow-hidden rounded-2xl border border-charcoal-border bg-charcoal-surface">
            {scan.photo_base64 && <img src={scan.photo_base64} alt="your scalp" className="h-full w-full object-cover" data-testid="report-photo" />}
          </div>

          <div className="lg:col-span-2 space-y-4" data-testid="category-ratings">
            {r.categories?.map((c, i) => (
              <motion.div
                key={c.key}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.08 }}
                className="rounded-xl border border-charcoal-border bg-charcoal-surface p-5"
                data-testid={`category-${c.key}`}
              >
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-heading text-xl text-cream">{c.name}</span>
                  <span className="text-sm text-gold">{c.label} · {c.score}/10</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-charcoal">
                  <div className={`h-full rounded-full ${barColor(c.score)} transition-all duration-700`} style={{ width: `${c.score * 10}%` }} />
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{c.observation}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Causes */}
        {r.causes?.length > 0 && (
          <div className="mt-14">
            <SectionTitle icon={Sparkles}>Observations</SectionTitle>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {r.causes.map((c, i) => (
                <div key={i} className="rounded-xl border border-charcoal-border bg-charcoal-surface p-6" data-testid={`cause-${i}`}>
                  <p className="mb-2 font-heading text-xl text-gold">{c.issue}</p>
                  <p className="text-sm leading-relaxed text-muted-foreground">{c.explanation}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Shop our store */}
        <div className="mt-14 flex flex-col items-start justify-between gap-6 rounded-2xl border border-gold/40 bg-charcoal-surface p-8 md:flex-row md:items-center md:p-10" data-testid="shop-store-card">
          <div className="flex items-start gap-5">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-gold/40 text-gold">
              <ShoppingBag className="h-6 w-6" strokeWidth={1.5} />
            </span>
            <div>
              <h2 className="font-heading text-3xl text-cream">Shop your hair care</h2>
              <p className="mt-1 max-w-md text-sm text-muted-foreground">
                For hair care products, shop our full range in the Carlocs store.
              </p>
            </div>
          </div>
          <a href={STORE_URL} target="_blank" rel="noopener noreferrer" data-testid="shop-store-btn">
            <Button className="rounded-full bg-gold px-7 py-6 text-base font-500 text-charcoal hover:bg-gold/90">
              <ShoppingBag className="mr-2 h-4 w-4" /> Shop Now
            </Button>
          </a>
        </div>

        {/* Book appointment — when any issue would benefit from an in-person visit */}
        {anyFlagged && (
          <div className="mt-14 flex flex-col items-start justify-between gap-6 rounded-2xl border border-gold/40 bg-charcoal-surface p-8 md:flex-row md:items-center md:p-10" data-testid="book-consult-card">
            <div className="flex items-start gap-5">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-gold/40 text-gold">
                <CalendarCheck className="h-6 w-6" strokeWidth={1.5} />
              </span>
              <div>
                <h2 className="font-heading text-3xl text-cream">{densityFlagged ? "Signs of thinning noticed" : "An in-person visit could help"}</h2>
                <p className="mt-1 max-w-md text-sm text-muted-foreground">
                  {densityFlagged
                    ? "Density looks lower than ideal. We recommend gentler retwist tension and an in-person consult with a Carlocs loctician to protect your edges and roots."
                    : "Based on your results, a hands-on session with a Carlocs loctician would help address these findings. Book a visit at your convenience."}
                </p>
              </div>
            </div>
            <a href={FRESHA_URL} target="_blank" rel="noopener noreferrer" data-testid="book-consult-btn">
              <Button className="rounded-full bg-gold px-7 py-6 text-base font-500 text-charcoal hover:bg-gold/90">
                <CalendarCheck className="mr-2 h-4 w-4" /> Book an appointment
              </Button>
            </a>
          </div>
        )}

        {/* Aftercare */}
        {r.aftercare_tips?.length > 0 && (
          <div className="mt-14 rounded-2xl border border-charcoal-border bg-charcoal-surface p-8 md:p-10">
            <SectionTitle>Aftercare ritual</SectionTitle>
            <ol className="space-y-4" data-testid="aftercare-tips">
              {r.aftercare_tips.map((t, i) => (
                <li key={i} className="flex gap-4">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gold/40 font-heading text-sm text-gold">{i + 1}</span>
                  <span className="text-sm leading-relaxed text-cream/90">{t}</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Shareable Crown Certificate */}
        <div className="mt-14 rounded-2xl border border-charcoal-border bg-charcoal-surface p-8 md:p-10" data-testid="certificate-section">
          <div className="mb-6 text-center">
            <h2 className="font-heading text-3xl text-cream">Share your Crown Score</h2>
            <p className="mt-1 text-sm text-muted-foreground">Download a card for your Instagram or WhatsApp status.</p>
          </div>
          <CrownCertificate
            score={r.overall_score}
            label={r.overall_label}
            name={user?.name || "Carlocs Member"}
            date={new Date(scan.created_at).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}
          />
        </div>

        <div className="mt-12 flex justify-center">
          <Button className="rounded-full bg-gold px-8 py-6 text-charcoal hover:bg-gold/90" onClick={() => navigate("/payment")} data-testid="new-scan-from-report">
            Run another scan · R79
          </Button>
        </div>
      </div>
      <Footer />
    </div>
  );
}
